import { Box, Grid } from '@material-ui/core';
import {
  DocumentationRequiredType,
  DocumentStatus,
  DocumentType,
  patchSetDocumentStatus,
} from '@nemuru-eng/js-api-client';
import { useEffect, useMemo, useState } from 'react';
import { useFormContext } from 'react-hook-form';

import { ConfirmationCheckbox } from '@/components/modules/DocumentsValidation/components/_shared/ConfirmationCheckbox';
import { Section } from '@/components/modules/DocumentsValidation/components/_shared/Section';
import { ValidationButtons } from '@/components/modules/DocumentsValidation/components/_shared/ValidationButtons';
import { useDocumentsValidationContext } from '@/components/modules/DocumentsValidation/contexts/DocumentsValidationContext';
import { useFetchValidationData } from '@/components/modules/DocumentsValidation/hooks/useFetchValidationData';
import { DocumentationRequiredMap } from '@/components/modules/DocumentsValidation/maps/documentationRequired.map';
import { ValidationDeclaredDataMap } from '@/components/modules/DocumentsValidation/maps/validationDeclaredData.map';
import { ValidationSchema } from '@/components/modules/DocumentsValidation/schemas/validation.schema';
import { ValidationStep } from '@/components/modules/DocumentsValidation/types/ValidationStep';
import { updateBorrowerDocumentationRequired } from '@/components/modules/DocumentsValidation/utils/updateBorrowerDocumentationRequired';
import { KycSecurityCheckList } from '@/components/modules/KYC/KYCLoan/BorrowerKYC/components/DocumentIdSection/components/KycSecurityCheckList';
import { CustomSkeleton } from '@/components/shared/components/CustomSkeleton';
import { DocumentVisualizer } from '@/components/shared/components/DocumentVisualizer';
import { Font } from '@/components/shared/components/Font';
import { FormWrapper } from '@/components/shared/components/FormWrapper';
import { FieldVariant } from '@/components/shared/components/NewContextForms/types/FieldVariant';
import { Text } from '@/components/shared/components/Text';

interface VideoIdValidationProps {
  onNext: () => void;
}

export const VideoIdValidation = (props: VideoIdValidationProps) => {
  return (
    <FormWrapper formSchema={ValidationSchema} defaultValues={{ confirmation: false }}>
      <VideoIdForm {...props} />
    </FormWrapper>
  );
};

const VideoIdForm = ({ onNext }: VideoIdValidationProps) => {
  const { loan, borrowerOnboardingDetails, borrowerDocumentationRequired, update } = useDocumentsValidationContext();

  const { documentIdFaceFile, biometricsFaceFile, securityChecks, passportFile, fetchVideoIdValidationData, loading } =
    useFetchValidationData();
  const { handleSubmit } = useFormContext();

  const { documentType } = ValidationDeclaredDataMap.toDocumentIdData(borrowerOnboardingDetails);

  const [loadingValidation, setLoadingValidation] = useState(false);
  useEffect(() => {
    fetchData();
  }, []);

  const allNeededFiles = useMemo(
    () => [
      documentType === DocumentType.NIE_EU ? passportFile : documentIdFaceFile,
      biometricsFaceFile,
      securityChecks,
    ],
    [documentIdFaceFile, biometricsFaceFile, securityChecks, passportFile],
  );

  const fetchData = async () => {
    await fetchVideoIdValidationData(loan, borrowerOnboardingDetails);
  };

  const onSubmit = async (validate: boolean) => {
    const files = DocumentationRequiredMap.getFilesByStep(
      borrowerDocumentationRequired,
      ValidationStep.VIDEO_ID_VALIDATION,
      documentType,
    );

    if (!files) {
      onNext();
      return;
    }

    const documentationRequiredId = borrowerDocumentationRequired.id;
    const unvalidatedFiles = files.filter((file) => file.status !== DocumentStatus.VALIDATED);

    try {
      setLoadingValidation(true);
      await Promise.all(
        unvalidatedFiles.map((file) => {
          return patchSetDocumentStatus({
            id: documentationRequiredId,
            fileTypeId: file.id,
            documentationRequiredType: DocumentationRequiredType.BORROWER,
            documentStatus: validate ? DocumentStatus.VALIDATED : DocumentStatus.INCIDENCE,
          });
        }),
      );
      updateBorrowerDocumentationRequired({
        borrowerDocumentationRequired,
        filesToUpdate: unvalidatedFiles,
        validate,
        update,
      });
    } catch (error) {
      console.error(error);
    } finally {
      setLoadingValidation(false);
    }

    onNext();
  };

  const allNeededFilesLoaded = allNeededFiles.every((file) => !!file);

  return (
    <Section.Content>
      <Section.Header title={`DOCUMENTS_VALIDATION_STEP_${ValidationStep.VIDEO_ID_VALIDATION}`} />
      <Grid container spacing={4}>
        <Grid item xs={12} lg={6}>
          <Box mb={1}>
            <Font variant="subtitle2" color="textSecondary">
              <Text tId={'DOCUMENT_ID_FACE_PICTURE'} />
            </Font>
          </Box>
          <DocumentVisualizer
            id={'visualizer-document-id-face'}
            documentFile={documentType === DocumentType.NIE_EU ? passportFile : documentIdFaceFile}
            loading={loading}
          />
        </Grid>
        <Grid item xs={12} lg={6}>
          <Box mb={1}>
            <Font variant="subtitle2" color="textSecondary">
              <Text tId={'BIOMETRICS_FACE_PICTURE'} />
            </Font>
          </Box>
          <DocumentVisualizer id={'visualizer-biometrics-face'} documentFile={biometricsFaceFile} loading={loading} />
        </Grid>
        <Grid item xs={12}>
          {securityChecks ? (
            <KycSecurityCheckList securityChecks={securityChecks} />
          ) : (
            <CustomSkeleton height={'208px'} />
          )}
        </Grid>
      </Grid>
      <ConfirmationCheckbox label={'VIDEO_ID_VALIDATION_CHECKBOX'} fieldVariant={FieldVariant.form} />
      <Section.Footer>
        <ValidationButtons
          onValidate={handleSubmit(() => onSubmit(true))}
          onDoNotValidate={() => onSubmit(false)}
          disabled={loading || loadingValidation || !allNeededFilesLoaded}
        />
      </Section.Footer>
    </Section.Content>
  );
};
